import { Network } from '@puzzlehq/sdk';
import { useAuth, useSettings } from '@src/data/settings';
import { trpcReact } from '@src/data/trpc';
import { LeaderboardDuration, LeaderboardType } from 'jigsaw';
import { useEffectiveAccount } from './useEffectiveAccount';
import toast from 'react-hot-toast';

const network = import.meta.env.VITE_NETWORK! as Network;

export const useQuests = () => {
  const { effectiveAddress } = useEffectiveAccount();
  return trpcReact.getQuests.useQuery({
    client: 'extension',
    network,
    address: effectiveAddress,
  });
};

export const useQuestProgress = (questId: string) => {
  const { effectiveAddress } = useEffectiveAccount();
  return trpcReact.getQuestProgress.useQuery(
    {
      address: effectiveAddress!,
      questId,
      network,
    },
    {
      enabled: !!effectiveAddress,
    }
  );
};

export const useParcDripTime = () => {
  const { effectiveAddress } = useEffectiveAccount();
  return trpcReact.getParcDripTime.useQuery(
    {
      address: effectiveAddress!,
      network,
    },
    {
      enabled: !!effectiveAddress,
    }
  );
};

export const useDailyQuestProgress = () => {
  const { effectiveAddress } = useEffectiveAccount();
  return trpcReact.getDailyQuestProgress.useQuery(
    {
      address: effectiveAddress!,
      network,
    },
    {
      enabled: !!effectiveAddress,
    }
  );
};

export const useReferralQuest = () => {
  const { effectiveAddress } = useEffectiveAccount();
  return trpcReact.getReferralQuest.useQuery(
    {
      address: effectiveAddress!,
      network,
    },
    {
      enabled: !!effectiveAddress,
    }
  );
};

export const useLeaderboard = ({
  duration,
  leaderboardType,
}: {
  duration: LeaderboardDuration;
  leaderboardType: LeaderboardType;
}) => {
  return trpcReact.getLeaderboard.useQuery(
    {
      network,
      duration,
      type: leaderboardType,
    },
    {
      staleTime: 5 * 60_000,
    }
  );
};

export const useNewUserQuests = () => {
  const { effectiveAddress } = useEffectiveAccount();
  const { auth, challengeAuth } = useAuth();

  const result = trpcReact.getNewUserQuests.useQuery(
    {
      network,
      address: effectiveAddress!,
      auth,
      challengeAuth,
    },
    {
      enabled: !!effectiveAddress && (!!auth || !!challengeAuth),
    }
  );

  return result;
};

export const useLatestRaffle = (enabled?: boolean) => {
  return trpcReact.getLatestGiveawayForCountry.useQuery(
    {
      network: import.meta.env.VITE_NETWORK,
    },
    {
      enabled: enabled,
    }
  );
};

export const useArcadeStoreSKUsForCountry = () => {
  const { countryCode } = useSettings();

  return trpcReact.getProductsSKUsForCountry.useQuery(
    {
      network,
      userCountry: countryCode,
    },
    {
      retry: 5,
      staleTime: 5_000 * 60,
    }
  );
};

export const useUserCountryCode = () => {
  return trpcReact.getUserCountryCode.useQuery(undefined, {
    staleTime: Infinity,
  });
};

export const useStreaks = () => {
  const { effectiveAddress } = useEffectiveAccount();
  const { auth, challengeAuth } = useAuth();

  return trpcReact.getStreakForUser.useQuery(
    {
      address: effectiveAddress!,
      network: import.meta.env.VITE_NETWORK!,
      auth: auth,
      challengeAuth: challengeAuth,
    },
    {
      enabled: !!effectiveAddress && !!(!!auth || !!challengeAuth),
      staleTime: 60_000,
      refetchOnWindowFocus: true, // Useful for browser tabs
      refetchOnMount: true, // This will work when WebView becomes visible again
      refetchOnReconnect: true, // Useful for network changes
    }
  );
};

export const useClaimStreakReward = () => {
  const { effectiveAddress } = useEffectiveAccount();
  const { auth, challengeAuth } = useAuth();
  const trpcReactUtils = trpcReact.useUtils();

  const { mutate, isLoading, isError, isSuccess } =
    trpcReact.claimStreakReward.useMutation({
      onSuccess: () => {
        toast.success('Claimed streak reward! It will arrive shortly.');
        trpcReactUtils.getStreakForUser.invalidate({
          address: effectiveAddress!,
          network: import.meta.env.VITE_NETWORK!,
          auth: auth,
          challengeAuth: challengeAuth,
        });
      },
      onError: (e) => {
        toast.error(`Couldn't claim streak reward: ${e.message}`);
      },
    });

  const claimStreakReward = () => {
    if (!effectiveAddress || !(!!auth || challengeAuth)) return;
    mutate({
      address: effectiveAddress,
      network: import.meta.env.VITE_NETWORK!,
      auth: auth!,
      challengeAuth: challengeAuth!,
    });
  };

  return {
    claimStreakReward,
    isLoading,
    isError,
    isSuccess,
  };
};

export const useDailyStats = () => {
  const { effectiveAddress } = useEffectiveAccount();
  const { auth, challengeAuth } = useAuth();

  return trpcReact.getDailyStats.useQuery(
    {
      address: effectiveAddress!,
      network: import.meta.env.VITE_NETWORK!,
      auth: auth,
      challengeAuth: challengeAuth,
    },
    {
      enabled: !!effectiveAddress && !!(!!auth || !!challengeAuth),
      staleTime: 60_000,
      refetchOnWindowFocus: true, // Useful for browser tabs
      refetchOnMount: true, // This will work when WebView becomes visible again
      refetchOnReconnect: true, // Useful for network changes
    }
  );
};
